import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useClientModulesAdmin } from '@/hooks/useClientModulesAdmin';
import { Package, Check, X } from 'lucide-react';

interface ClientModulesDialogProps {
  clientId: string | null;
  clientName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ClientModulesDialog = ({
  clientId,
  clientName,
  open,
  onOpenChange,
}: ClientModulesDialogProps) => {
  const {
    modules,
    isLoading,
    toggleModule,
    isModuleActive,
    availableModules,
    isActivating,
    isDeactivating,
  } = useClientModulesAdmin(clientId || undefined);

  const handleToggle = async (moduleCode: string, checked: boolean) => {
    if (!clientId) return;
    await toggleModule(clientId, moduleCode, checked);
  };

  const activeCount = modules.filter(m => m.is_active).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Gerenciar Módulos
          </DialogTitle>
          <DialogDescription>
            Configure os módulos ativos para <strong>{clientName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-muted-foreground">
              Módulos ativos: {activeCount} de {availableModules.length}
            </span>
            <Badge variant="outline">
              {Math.round((activeCount / availableModules.length) * 100)}% ativo
            </Badge>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {availableModules.map((module) => {
                const isActive = isModuleActive(module.code);
                const isPending = isActivating || isDeactivating;

                return (
                  <div
                    key={module.code}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                      isActive ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                        isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>
                        {isActive ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{module.name}</p>
                        <p className="text-sm text-muted-foreground">{module.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={isActive}
                      onCheckedChange={(checked) => handleToggle(module.code, checked)}
                      disabled={isPending}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
